import { ref, type Ref } from 'vue'
import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosResponseHeaders,
  type ResponseType,
} from 'axios'
import type { FetchMethod } from '../../models/api/fetch-method'
import { useAuthStore } from '../../../stores/auth.store'
import { useOrganizationStore } from '../../../stores/organization.store'
import { ApiRequestStatus } from '../../models/api/api-request-status'

type UseApiClientOptions<TBody> =
  | UsePostOptions<TBody>
  | UseFetchOptions
  | UsePutOptions<TBody>
  | UseDeleteOptions<TBody>

interface BaseOptions {
  endpoint: string
  omitOrganization: boolean
  responseType?: ResponseType
  query?: { [key: string]: string | boolean | number | string[] }
  headers?: Record<string, string>
}

interface UsePostOptions<TBody> extends BaseOptions {
  method: FetchMethod.POST
  body?: TBody
}

interface UseFetchOptions extends BaseOptions {
  method: FetchMethod.GET
}

interface UsePutOptions<TBody> extends BaseOptions {
  method: FetchMethod.PUT
  body?: TBody
}

interface UseDeleteOptions<TBody> extends BaseOptions {
  method: FetchMethod.DELETE
  body?: TBody
}

export function useApiClient<TOutput, TBody = unknown>(): {
  data: Ref<TOutput>
  status: Ref<ApiRequestStatus>
  error: Ref<Error>
  responseHeaders: Ref<AxiosResponseHeaders | undefined>
  execute: (options: UseApiClientOptions<TBody>) => void
} {
  const data = ref()
  const status = ref<ApiRequestStatus>(ApiRequestStatus.Idle)
  const error = ref()
  const responseHeaders = ref<AxiosResponseHeaders | undefined>(undefined)

  const authStore = useAuthStore()

  const execute = async (options: UseApiClientOptions<TBody>) => {
    status.value = ApiRequestStatus.Loading
    error.value = null
    try {
      const requestConfig = createRequestConfig(options)

      let orgId: string | undefined
      if (!options.omitOrganization) {
        const { currentOrganization } = useOrganizationStore()
        orgId = currentOrganization.id
      }

      const endpointUrl = buildEndpointUrl(
        options.endpoint,
        orgId,
        options.omitOrganization,
        options.query,
      )

      const response: AxiosResponse = await axios(endpointUrl, requestConfig)

      responseHeaders.value = response.headers as AxiosResponseHeaders

      if (response.status === 204) {
        data.value = undefined
        status.value = ApiRequestStatus.Success
        return
      }

      const contentType = response.headers['content-type'] as string

      if (
        contentType &&
        (contentType.includes('application/json') || contentType.includes('text/html'))
      ) {
        data.value = response.data
      } else {
        data.value = new Blob([response.data], { type: response.headers['content-type'] })
      }

      status.value = ApiRequestStatus.Success
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const axiosError = err as any
      const responseStatus = axiosError.response?.status
      if (responseStatus >= 502 && responseStatus <= 504) {
        status.value = ApiRequestStatus.Unavailable
      } else {
        error.value = err instanceof Error ? err : new Error('Unknown error')
        status.value = ApiRequestStatus.Error
      }

      console.log(err)
    }
  }

  const createRequestConfig = (options: UseApiClientOptions<TBody>): AxiosRequestConfig => {
    const headers: Record<string, string> = {
      ...(options.headers ?? {}),
    }

    if (authStore.isAuthenticated && authStore.token) {
      headers['Authorization'] = `Bearer ${authStore.token}`
    }

    const config: AxiosRequestConfig = {
      method: options.method,
      headers,
      timeout: 10000,
      ...(options.responseType ? { responseType: options.responseType } : {}),
    }

    if ('body' in options && options.body) {
      config.data = options.body
    }

    return config
  }

  const buildEndpointUrl = (
    endpoint: string,
    orgId: string | undefined,
    omitOrg: boolean,
    query?: { [key: string]: string | boolean | number | string[] },
  ): string => {
    let q = ''
    if (query) {
      q = buildQueryString(query)
    }

    if (!omitOrg && orgId) {
      return `/api/org/${orgId}/${endpoint}${q}`
    }
    return `/api/${endpoint}${q}`
  }

  const buildQueryString = (query: { [key: string]: string | boolean | number | string[] }) => {
    return (
      '?' +
      Object.keys(query)
        .map((key) => {
          const isArray = Array.isArray(query[key])
          if (isArray) {
            return arrayToQueryString(key, query[key] as string[])
          } else {
            const k = encodeURIComponent(key)
            const v = encodeURIComponent(query[key] as number | string | boolean)
            return `${k}=${v}`
          }
        })
        .join('&')
    )
  }

  const arrayToQueryString = (paramName: string, arr: string[]) => {
    if (!arr || arr.length === 0) {
      return ''
    }

    const queryString = arr
      .map((item) => `${encodeURIComponent(paramName)}[]=${encodeURIComponent(item)}`)
      .join('&')
    return `${queryString}`
  }

  return { data, status, error, execute, responseHeaders }
}
